import { RootState } from '@next/core-lib/app';
import { EntityState } from '@ngrx/entity';

export interface SiteState extends RootState {
  app?: AppState;
}

export interface PaginationState {
  pageNumber: number;
  pageSize: number;
  itemCount: number;
}

export interface AppState {
  crops?: Crop[];
  cropVarieties?: CropVariety[];
  cropAids?: CropAid[];
  salesOrderPeriods?: SalesOrderPeriod[];
  soilTypes?: SoilType[];
  totalNitrogenLegalNormPagination?: PaginationState;
  totalNitrogenLegalNormFilter?: TotalNitrogenLegalNormFilter;
  totalNitrogenLegalNorm?: TotalNitrogenLegalNormState;
  legalNitrogenWorkingCoefficientsPagination?: PaginationState;
  legalNitrogenWorkingCoefficient?: LegalNitrogenWorkingCoefficientState;
  legalNitrogenWorkingCoefficientsFilter?: LegalNitrogenWorkingCoefficientsFilter;
  cropVarietySpecificNormPagination?: PaginationState;
  cropVarietySpecificNormFilter?: CropVarietySpecificNormFilter;
  cropVarietySpecificNorm?: CropVarietySpecificNormState;
  phosphateLegalNormPagination?: PaginationState;
  phosphateLegalNormFilter?: PhosphateLegalNormFilter;
  phosphateLegalNorm?: PhosphateLegalNormState;
  phosphateLegalWorkingCoefficientsPagination?: PaginationState;
  phosphateLegalWorkingCoefficient?: PhosphateLegalWorkingCoefficientState;
  phosphateLegalWorkingCoefficientsFilter?: PhosphateLegalWorkingCoefficientsFilter;
}

export interface TotalNitrogenLegalNormState extends EntityState<TotalNitrogenLegalNorm> {}

export interface LegalNitrogenWorkingCoefficientsState extends EntityState<LegalNitrogenWorkingCoefficient> {}

export interface LegalNitrogenWorkingCoefficientState extends EntityState<LegalNitrogenWorkingCoefficient> {}

export interface CropVarietySpecificNormState extends EntityState<CropVarietySpecificNorm> {}

export interface PhosphateLegalNormState extends EntityState<PhosphateLegalNorm> {}

export interface PhosphateLegalWorkingCoefficientsState extends EntityState<PhosphateLegalWorkingCoefficient> {}

export interface PhosphateLegalWorkingCoefficientState extends EntityState<PhosphateLegalWorkingCoefficient> {}

export enum PageTitles {
  Import = 'import',
  TotalNitrogenLegalNorm = 'totalNitrogenLegalNorm',
  CropVarietySpecificNorm = 'cropVarietySpecificNorm',
  PhosphateLegalNorm = 'phosphateLegalNorm',
  PhosphateTotalLegalNorm = 'phosphateTotalLegalNorm',
}

export enum LocationInCountry {
  South = 'South',
  Other = 'Other',
}

export enum GrasslandUsage {
  Mow = 'Mow',
  Graze = 'Graze',
}

export enum CropVarietySpecificNormValue {
  Low = 'Low',
  High = 'High',
}

export enum PhosphateCondition {
  Unknown = 'Unknown',
  Poor = 'Poor',
  Low = 'Low',
  Neutral = 'Neutral',
  Rich = 'Rich',
  High = 'High',
}

export enum ImportType {
  totalNitrogenLegalNorm = 'TotalNitrogenLegalNorm',
  legalNitrogenWorkingCoefficient = 'LegalNitrogenWorkingCoefficient',
  cropVarietySpecificNorms = 'CropVarietySpecificNorms',
  phosphateLegalNorm = 'PhosphateLegalNorm',
}

export interface AgrifirmCrop {
  id: number;
  name: string;
}

export interface CrudeCrop {
  id: number;
  agrifirmCrop: AgrifirmCrop;
}

export interface GetCropsResponse {
  crops: CrudeCrop[];
}

export interface Crop {
  id: number;
  name: string;
  agrifirmCropId: number;
}

export interface CropVariety {
  id: number;
  name: string;
  ediCode: number;
}

export interface CropAid {
  id: number;
  name: string;
}

export interface GetCropVarietiesResponse {
  cropVarieties: CropVariety[];
}

export enum FertilizerTypeEnum {
  Synthetic = 'Synthetic',
  Animal = 'Animal',
  OtherOrganic = 'OtherOrganic',
  Compost = 'Compost',
  Lime = 'Lime',
  BioStimulator = 'BioStimulator',
}

export enum TreatmentType {
  GBM = 'GBM',
  Fertilization = 'Fertilization',
}

export interface SalesOrderPeriod {
  id: number;
  year: number;
  periodNumber: number;
  name: string;
  startDispatchFrom: string | Date;
  deliverBefore: string | Date;
  periodType: TreatmentType;
}

export interface SoilType {
  id: number;
  name: string;
}

export interface TotalNitrogenLegalNorm {
  id: number;
  cropId: number;
  calendarYear: number;
  agrifirmCropId: number;
  cropName: string;
  soilTypeId: number;
  nitrogenNorm: number;
  location: string;
  isPreviousCropCorn: boolean;
  isPreviousCropNotGrain: boolean;
  grasslandUsage: string;
  shouldSplitNorm: boolean;
  cropVarietySpecificNorm: string;
  startDateBefore?: Date;
  startDateAfter?: Date;
  endDateBefore?: Date;
  endDateAfter?: Date;
  startDatePreviousYearBefore?: Date;
  startDatePreviousYearAfter?: Date;
  isNitrogenDifferentiation: boolean;
  isAfterTornGrass: boolean;
  isGroundWaterPreservationWithNitrogenCuts: boolean;
  isNutrientsPollutedArea: boolean;
}

export interface TotalNitrogenLegalNormFilter {
  year?: number;
  cropId?: number;
  [key: string]: string | number | string[] | number[];
}

export interface TotalNitrogenLegalNormFilterItem {
  cropId: number;
  cropName: string;
  norm: TotalNitrogenLegalNorm;
}

export interface GetTotalNitrogenLegalNormsResponse {
  totalNitrogenLegalNorms: TotalNitrogenLegalNorm[];
  pagination: PaginationState;
}

export interface UpsertTotalNitrogenLegalNormResponse {
  id: number;
}

export interface DeleteTotalNitrogenLegalNormResponse {
  id: number;
}

export interface CropVarietySpecificNormFilter {
  year?: number;
  [key: string]: string | number | string[] | number[];
}

export interface CropVarietySpecificNorm {
  id: number;
  calendarYear: number;
  cropId: number;
  agrifirmCropId: number;
  cropName: string;
  cropVarietyId: number;
  ediCode: number;
  cropVarietyName: string;
  cropVarietySpecificNorm: CropVarietySpecificNormValue;
  isNitrogenDifferentiation: boolean;
}
export interface CropVarietySpecificNormFilterItem {
  cropId: number;
  cropName: string;
  norm: CropVarietySpecificNorm;
}

export interface GetCropVarietySpecificNormsResponse {
  cropVarietySpecificNorms: CropVarietySpecificNorm[];
  pagination: PaginationState;
}

export interface UpsertCropVarietySpecificNormResponse {
  id: number;
}

export interface DeleteCropVarietySpecificNormResponse {
  id: number;
}

export interface LegalNitrogenWorkingCoefficient {
  id: number;
  calendarYear: number;
  nitrogenWorkingCoefficient: number;
  fertilizerType: string;
  cropAidId: number;
  soilTypeId: number;
  salesOrderPeriodId: number;
}

export interface PhosphateLegalWorkingCoefficient {
  id: number;
  calendarYear: number;
  phosphateWorkingCoefficient: number;
  cropAidId: number;
}

export interface PhosphateLegalWorkingCoefficientsFilter {
  year?: number;
  [key: string]: string | number | string[] | number[];
}

export interface LegalNitrogenWorkingCoefficientsFilter {
  year?: number;
  [key: string]: string | number | string[] | number[];
}

export interface GetPhosphateLegalWorkingCoefficients {
  phosphateLegalWorkingCoefficients: PhosphateLegalWorkingCoefficient[];
  pagination: PaginationState;
}

export interface UpsertPhosphateLegalWorkingCoefficientResponse {
  id: number;
}

export interface DeletePhosphateLegalWorkingCoefficientResponse {
  id: number;
}

export interface GetLegalNitrogenWorkingCoefficients {
  legalNitrogenWorkingCoefficients: LegalNitrogenWorkingCoefficient[];
  pagination: PaginationState;
}

export interface UpsertLegalNitrogenWorkingCoefficientResponse {
  id: number;
}

export interface DeleteLegalNitrogenWorkingCoefficientResponse {
  id: number;
}

export interface PhosphateLegalNorm {
  id: number;
  cropId: number;
  agrifirmCropId: number;
  calendarYear: number;
  pCaClFromAndIncluding: number;
  pCaClToAndIncluding: number;
  pAlFromAndIncluding: number;
  pAlToAndIncluding: number;
  pwFromAndIncluding: number;
  pwToAndIncluding: number;
  condition: PhosphateConditionEnum;
  phosphateNorm: number;
  poorInPhosphateNorm: number;
  isTransitionVariant: boolean;
}

export enum PhosphateConditionEnum {
  Unknown = 'Unknown',
  Poor = 'Poor',
  Low = 'Low',
  Neutral = 'Neutral',
  Rich = 'Rich',
  High = 'High',
}

export interface PhosphateLegalNormFilter {
  year?: number;
  [key: string]: string | number | string[] | number[];
}

export interface PhosphateLegalNormFilterItem {
  cropId: number;
  cropName: string;
  norm: PhosphateLegalNorm;
}

export interface GetPhosphateLegalNormsResponse {
  phosphateLegalNorms: PhosphateLegalNorm[];
  pagination: PaginationState;
}

export interface UpsertPhosphateLegalNormResponse {
  id: number;
}

export interface DeletePhosphateLegalNormResponse {
  id: number;
}
